import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Spinner, Button } from '@bbpos/ui';
import { LoginPage } from '@/pages/LoginPage';
import { BillingPage } from '@/pages/BillingPage';
import { BillHistoryPage } from '@/pages/BillHistoryPage';
import { BillDetailPage } from '@/pages/BillDetailPage';
import { ReturnsPage } from '@/pages/ReturnsPage';
import { ProductCatalogPage } from '@/pages/ProductCatalogPage';
import { ProductDetailPage } from '@/pages/ProductDetailPage';
import { PosLayout } from '@/components/common/PosLayout';
import { useAuthStore } from '@/stores/use-auth-store';
import { isManagerPlus } from '@/lib/sync-constants';
import type { PlanFeatureKey } from '@bbpos/types';
import { usePlanFeature } from '@/hooks/use-plan-feature';
import { LockedFeature } from '@/components/common/LockedFeature';

// Lazy-loaded pages for code splitting
const CustomerLookupPage = lazy(() => import('@/pages/CustomerLookupPage').then((m) => ({ default: m.CustomerLookupPage })));
const CustomerDetailPage = lazy(() => import('@/pages/CustomerDetailPage').then((m) => ({ default: m.CustomerDetailPage })));
const OpenShiftPage = lazy(() => import('@/pages/OpenShiftPage').then((m) => ({ default: m.OpenShiftPage })));
const CloseShiftPage = lazy(() => import('@/pages/CloseShiftPage').then((m) => ({ default: m.CloseShiftPage })));
const RegisterPage = lazy(() => import('@/pages/RegisterPage').then((m) => ({ default: m.RegisterPage })));
const SettingsPage = lazy(() => import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const SyncStatusPage = lazy(() => import('@/pages/SyncStatusPage').then((m) => ({ default: m.SyncStatusPage })));
const ActiveOrdersPage = lazy(() => import('@/pages/ActiveOrdersPage').then((m) => ({ default: m.ActiveOrdersPage })));
const NewOrderPage = lazy(() => import('@/pages/NewOrderPage').then((m) => ({ default: m.NewOrderPage })));
const OrderDetailPage = lazy(() => import('@/pages/OrderDetailPage').then((m) => ({ default: m.OrderDetailPage })));
const FloorPlanPage = lazy(() => import('@/pages/FloorPlanPage').then((m) => ({ default: m.FloorPlanPage })));
const ReservationsPage = lazy(() => import('@/pages/ReservationsPage').then((m) => ({ default: m.ReservationsPage })));
const WaitlistPage = lazy(() => import('@/pages/WaitlistPage').then((m) => ({ default: m.WaitlistPage })));
const RecipeListPage = lazy(() => import('@/pages/RecipeListPage').then((m) => ({ default: m.RecipeListPage })));
const RecipeDetailPage = lazy(() => import('@/pages/RecipeDetailPage').then((m) => ({ default: m.RecipeDetailPage })));
const VoicePosPage = lazy(() => import('@/pages/VoicePosPage').then((m) => ({ default: m.VoicePosPage })));
const FestiveOrdersPage = lazy(() => import('@/pages/FestiveOrdersPage').then((m) => ({ default: m.FestiveOrdersPage })));
const CashClosingPage = lazy(() => import('@/pages/CashClosingPage').then((m) => ({ default: m.CashClosingPage })));
const QuotationListPage = lazy(() => import('@/pages/QuotationListPage').then((m) => ({ default: m.QuotationListPage })));
const OnlineOrdersPage = lazy(() => import('@/pages/OnlineOrdersPage').then((m) => ({ default: m.OnlineOrdersPage })));
const OnlineOrderDetailPage = lazy(() => import('@/pages/OnlineOrderDetailPage').then((m) => ({ default: m.OnlineOrderDetailPage })));
const QuotationDetailPage = lazy(() => import('@/pages/QuotationDetailPage').then((m) => ({ default: m.QuotationDetailPage })));
const NotificationListPage = lazy(() => import('@/pages/NotificationListPage').then((m) => ({ default: m.NotificationListPage })));
const NotificationDetailPage = lazy(() => import('@/pages/NotificationDetailPage').then((m) => ({ default: m.NotificationDetailPage })));
const QuickAddProductPage = lazy(() => import('@/pages/QuickAddProductPage').then((m) => ({ default: m.QuickAddProductPage })));
const StockTransferListPage = lazy(() => import('@/pages/StockTransferListPage').then((m) => ({ default: m.StockTransferListPage })));
const StockTransferDetailPage = lazy(() => import('@/pages/StockTransferDetailPage').then((m) => ({ default: m.StockTransferDetailPage })));
const MenuTokensPage = lazy(() => import('@/pages/MenuTokensPage').then((m) => ({ default: m.MenuTokensPage })));

// Public pages (no auth, standalone, customer-facing)
const SelfBookingPage = lazy(() => import('@/pages/SelfBookingPage').then((m) => ({ default: m.SelfBookingPage })));
const ReOrderPage = lazy(() => import('@/pages/ReOrderPage').then((m) => ({ default: m.ReOrderPage })));
const QuoteApprovalPage = lazy(() => import('@/pages/QuoteApprovalPage').then((m) => ({ default: m.QuoteApprovalPage })));
const FeedbackPage = lazy(() => import('@/pages/FeedbackPage').then((m) => ({ default: m.FeedbackPage })));
const DigitalMenuPage = lazy(() => import('@/pages/DigitalMenuPage').then((m) => ({ default: m.DigitalMenuPage })));
const CreditNoteVerifyPage = lazy(() => import('@/pages/CreditNoteVerifyPage').then((m) => ({ default: m.CreditNoteVerifyPage })));
const UnsubscribePage = lazy(() => import('@/pages/UnsubscribePage').then((m) => ({ default: m.UnsubscribePage })));

const LazyFallback = (
  <div className="flex items-center justify-center min-h-screen">
    <Spinner variant="page" size="md" label="Loading..." />
  </div>
);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  const isHydrated = useAuthStore((s) => s.isHydrated);

  if (!isHydrated || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner variant="page" size="md" label="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    const returnUrl = window.location.pathname + window.location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(returnUrl)}`} replace />;
  }

  return <>{children}</>;
}

// PAGES.md role-level route guard. Blocks access before the page renders.
function RequireManagerPlus({ children }: { children: React.ReactNode }) {
  const profile = useAuthStore((s) => s.profile);
  const isHydrated = useAuthStore((s) => s.isHydrated);

  if (!isHydrated || !profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner variant="page" size="md" label="Loading..." />
      </div>
    );
  }

  if (!isManagerPlus(profile.role)) {
    return <Navigate to="/pos/billing" replace />;
  }

  return <>{children}</>;
}

// Plan lock (docs/plan-limits-plan.md §7): a feature outside the plan opens on
// LockedFeature instead of the page, so the page bundle is never fetched.
function RequirePlanFeature({ feature, children }: { feature: PlanFeatureKey; children: React.ReactNode }) {
  const allowed = usePlanFeature(feature);
  return allowed ? <>{children}</> : <LockedFeature feature={feature} />;
}

function RootRedirect() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isHydrated = useAuthStore((s) => s.isHydrated);

  if (!isHydrated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner variant="page" size="md" label="Loading..." />
      </div>
    );
  }

  return isAuthenticated ? (
    <Navigate to="/pos/billing" replace />
  ) : (
    <Navigate to="/login" replace />
  );
}

function NotFoundScreen({ message = 'Page not found' }: { message?: string }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center min-h-full px-4 py-12" role="main" aria-label={message}>
      <div className="text-center space-y-4">
        <p className="text-5xl font-bold text-neutral-300 dark:text-neutral-600">404</p>
        <p className="text-lg text-neutral-600 dark:text-neutral-400">{message}</p>
        <div className="flex gap-3 justify-center pt-2">
          <Button variant="primary" onClick={() => navigate('/pos/billing', { replace: true })}>
            Go to Billing
          </Button>
          <Button variant="ghost" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<LoginPage />} />

      {/* All /pos/* routes share PosLayout (status bar) and require auth */}
      <Route
        path="/pos"
        element={
          <ProtectedRoute>
            <PosLayout />
          </ProtectedRoute>
        }
      >
        <Route path="billing" element={<BillingPage />} />
        <Route path="bills" element={<BillHistoryPage />} />
        <Route path="bills/:id" element={<BillDetailPage />} />
        <Route path="returns" element={<ReturnsPage />} />
        <Route path="products" element={<ProductCatalogPage />} />
        <Route path="products/:id" element={<ProductDetailPage />} />
        <Route
          path="customers"
          element={
            <Suspense fallback={LazyFallback}>
              <CustomerLookupPage />
            </Suspense>
          }
        />
        <Route
          path="customers/:id"
          element={
            <Suspense fallback={LazyFallback}>
              <CustomerDetailPage />
            </Suspense>
          }
        />
        <Route
          path="shift/open"
          element={
            <Suspense fallback={LazyFallback}>
              <OpenShiftPage />
            </Suspense>
          }
        />
        <Route
          path="shift/close"
          element={
            <Suspense fallback={LazyFallback}>
              <CloseShiftPage />
            </Suspense>
          }
        />
        <Route
          path="register"
          element={
            <Suspense fallback={LazyFallback}>
              <RegisterPage />
            </Suspense>
          }
        />
        <Route
          path="settings"
          element={
            <Suspense fallback={LazyFallback}>
              <SettingsPage />
            </Suspense>
          }
        />
        <Route
          path="sync"
          element={
            <RequireManagerPlus>
              <Suspense fallback={LazyFallback}>
                <SyncStatusPage />
              </Suspense>
            </RequireManagerPlus>
          }
        />
        <Route
          path="orders"
          element={
            <Suspense fallback={LazyFallback}>
              <ActiveOrdersPage />
            </Suspense>
          }
        />
        <Route
          path="orders/new"
          element={
            <Suspense fallback={LazyFallback}>
              <NewOrderPage />
            </Suspense>
          }
        />
        <Route
          path="orders/:id"
          element={
            <Suspense fallback={LazyFallback}>
              <OrderDetailPage />
            </Suspense>
          }
        />
        <Route
          path="tables"
          element={
            <Suspense fallback={LazyFallback}>
              <FloorPlanPage />
            </Suspense>
          }
        />
        <Route
          path="reservations"
          element={
            <Suspense fallback={LazyFallback}>
              <ReservationsPage />
            </Suspense>
          }
        />
        <Route
          path="waitlist"
          element={
            <Suspense fallback={LazyFallback}>
              <WaitlistPage />
            </Suspense>
          }
        />
        <Route
          path="recipes"
          element={
            <Suspense fallback={LazyFallback}>
              <RecipeListPage />
            </Suspense>
          }
        />
        <Route
          path="recipes/:id"
          element={
            <Suspense fallback={LazyFallback}>
              <RecipeDetailPage />
            </Suspense>
          }
        />
        <Route
          path="festive-orders"
          element={
            <RequirePlanFeature feature="festive">
              <Suspense fallback={LazyFallback}>
                <FestiveOrdersPage />
              </Suspense>
            </RequirePlanFeature>
          }
        />
        <Route
          path="voice"
          element={
            <RequirePlanFeature feature="voice_pos">
              <Suspense fallback={LazyFallback}>
                <VoicePosPage />
              </Suspense>
            </RequirePlanFeature>
          }
        />
        <Route
          path="notifications"
          element={
            <Suspense fallback={LazyFallback}>
              <NotificationListPage />
            </Suspense>
          }
        />
        <Route
          path="notifications/:id"
          element={
            <Suspense fallback={LazyFallback}>
              <NotificationDetailPage />
            </Suspense>
          }
        />
        {/*
          The online order queue. Lazy, like every other secondary screen: a
          shop with no website should not pay for this bundle on every till
          start, and the route simply 404s at the API for them.
        */}
        <Route
          path="online-orders"
          element={
            <RequirePlanFeature feature="online_store">
              <Suspense fallback={LazyFallback}>
                <OnlineOrdersPage />
              </Suspense>
            </RequirePlanFeature>
          }
        />
        <Route
          path="online-orders/:id"
          element={
            <RequirePlanFeature feature="online_store">
              <Suspense fallback={LazyFallback}>
                <OnlineOrderDetailPage />
              </Suspense>
            </RequirePlanFeature>
          }
        />
        <Route
          path="quotations"
          element={
            <RequirePlanFeature feature="quotations">
              <Suspense fallback={LazyFallback}>
                <QuotationListPage />
              </Suspense>
            </RequirePlanFeature>
          }
        />
        <Route
          path="quotations/:id"
          element={
            <RequirePlanFeature feature="quotations">
              <Suspense fallback={LazyFallback}>
                <QuotationDetailPage />
              </Suspense>
            </RequirePlanFeature>
          }
        />
        <Route
          path="cash-closing"
          element={
            <Suspense fallback={LazyFallback}>
              <CashClosingPage />
            </Suspense>
          }
        />
        {/* /pos/inventory redirects to the product catalog (shared — both verticals) */}
        <Route path="inventory" element={<Navigate to="/pos/products" replace />} />
        <Route
          path="inventory/quick-add"
          element={
            <Suspense fallback={LazyFallback}>
              <QuickAddProductPage />
            </Suspense>
          }
        />
        {/* Stock Transfers — MANAGER+ (PAGES.md §2.23, §2.24) */}
        <Route
          path="stock-transfer"
          element={
            <RequireManagerPlus>
              <RequirePlanFeature feature="stock_transfers">
                <Suspense fallback={LazyFallback}>
                  <StockTransferListPage />
                </Suspense>
              </RequirePlanFeature>
            </RequireManagerPlus>
          }
        />
        <Route
          path="stock-transfer/:id"
          element={
            <RequireManagerPlus>
              <RequirePlanFeature feature="stock_transfers">
                <Suspense fallback={LazyFallback}>
                  <StockTransferDetailPage />
                </Suspense>
              </RequirePlanFeature>
            </RequireManagerPlus>
          }
        />
        {/* G4.5 — Menu QR management (MANAGER+) */}
        <Route
          path="menu-qr"
          element={
            <RequireManagerPlus>
              <Suspense fallback={LazyFallback}>
                <MenuTokensPage />
              </Suspense>
            </RequireManagerPlus>
          }
        />
        <Route path="*" element={<NotFoundScreen message="This POS page doesn't exist." />} />
      </Route>

      {/* ─── Public pages (no auth, standalone, customer-facing) ─── */}
      {/* SECURITY BOUNDARY: /book/* is intentionally outside ProtectedRoute.
          Public-facing QR / WhatsApp link entry points. Rate-limited server-side. */}
      <Route
        path="/book/:token"
        element={<Suspense fallback={LazyFallback}><SelfBookingPage /></Suspense>}
      />
      <Route
        path="/book/:token/status/:bookingNumber"
        element={<Suspense fallback={LazyFallback}><SelfBookingPage /></Suspense>}
      />
      {/* Legacy QR format kept for links printed before token migration */}
      <Route
        path="/book/:tenantSlug/:eventId"
        element={<Suspense fallback={LazyFallback}><SelfBookingPage /></Suspense>}
      />
      {/* SECURITY — :token is a 64-hex opaque token from the reorder_tokens
          table; never the enumerable bill_ref (e.g. MUM-20261025-0001) which
          could be guessed. ParseTokenPipe rejects any other shape server-side. */}
      <Route
        path="/reorder/:token"
        element={<Suspense fallback={LazyFallback}><ReOrderPage /></Suspense>}
      />
      <Route
        path="/quote/:quoteRef"
        element={<Suspense fallback={LazyFallback}><QuoteApprovalPage /></Suspense>}
      />
      <Route
        path="/feedback/:token"
        element={<Suspense fallback={LazyFallback}><FeedbackPage /></Suspense>}
      />
      {/* G4.5 — public digital menu (QR). 64-hex token from menu_tokens table;
          long-lived, MANAGER+ can rotate via POST /menu/tokens. */}
      <Route
        path="/menu/:token"
        element={<Suspense fallback={LazyFallback}><DigitalMenuPage /></Suspense>}
      />
      {/* T04 — the QR on a cash-refund credit note. :sig is an HMAC of :id
          issued by the API; the bill id alone opens nothing. */}
      <Route
        path="/credit-note/:id/:sig"
        element={<Suspense fallback={LazyFallback}><CreditNoteVerifyPage /></Suspense>}
      />
      {/* CU4-003 — the unsubscribe link in a marketing message. :sig is an
          HMAC of the customer id issued by the API; the id alone opens nothing. */}
      <Route
        path="/unsubscribe/:id/:sig"
        element={<Suspense fallback={LazyFallback}><UnsubscribePage /></Suspense>}
      />

      <Route path="*" element={<NotFoundScreen />} />
    </Routes>
  );
}
